import { useState, useEffect, useContext } from 'react'
import { useNavigate, useParams } from "react-router-dom"

import _ from "lodash"

import { enqueueSnackbar } from 'notistack'

import Body from './body'

import { AppContext } from "/providers/app-context"

import loadTemplate from '/utils/template-loader'
import { saveAppleModel, saveAppleTemplate, saveGoogleData, saveInstanceData, saveMetaData, saveGoogleClass, saveGoogleObject, saveScannerConfiguration } from '/providers/templates-api'
import ManageInstanceData from '/utils/manage-instance-data'

let skipGoogleClassSave = false
let skipGoogleObjectSave = false


const Editor = () => {
  const { apiSource, apiKey,user, setUser, refreshTemplates } = useContext(AppContext)

  const navigate = useNavigate()
  const { templateId } = useParams()

  const [templateLoaded, setTemplateLoaded] = useState(false)

  const [smartixPassType, setSmartixPassType] = useState(null)
  const [applePassType, setApplePassType] = useState(null)
  const [appleSubType, setAppleSubType] = useState(null)
  const [googlePassType, setGooglePassType] = useState(null)
  const [googleSubType, setGoogleSubType] = useState(null)

  const [appleModel, setAppleModel] = useState()
  const [appleTemplate, setAppleTemplate] = useState()
  const [googleBoth, setGoogleBoth] = useState()
  const [googleClass, setGoogleClass] = useState()
  const [googleObject, setGoogleObject] = useState()
  const [instanceData, setInstanceData] = useState({})
  const [scannerData, setScannerData] = useState({})
  const [scannerInstanceData,setScannerInstanceData] = useState({})
  const [isSmartixScannerPass, setIsSmartixScannerPass] = useState(false)
  const [metaData, setMetaData] = useState({})
  const [classes, setClasses] = useState("")
  const [showManageInstanceData, setShowManageInstanceData] = useState(false)

  // Prevent hot refresh from causing data saves
  if (import.meta.hot) {
    import.meta.hot.on('vite:beforeUpdate', () => {
      setTemplateLoaded(false)
    })
    import.meta.hot.on('vite:afterUpdate', () => {
      setTemplateLoaded(true)
    })
  }

  useEffect(() => {
    if (templateId === null) navigate("/templates", { replace: true })

    loadInitialData()

    if (user.preferences.highlight_editable_items) {
      let u = _.cloneDeep(user)
      u.preferences.highlight_editable_items = false
      setUser(u)

      setTimeout(() => {
        let u = _.cloneDeep(user)
        u.preferences.highlight_editable_items = true
        setUser(u)
      }, 2000) // Turn off then on again to ensure all fields change colour at same time.
    }
  }, [])

  useEffect(() => {
    setClasses(user.preferences.highlight_editable_items ? "showEditFields h-full w-full" : "h-full w-full")
  }, [user.preferences.highlight_editable_items])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving Apple model', variant: 'light' })
        let result = await saveAppleModel({ apiSource, apiKey, templateId, appleModel })

        if (result.ok) {
          enqueueSnackbar({ message: 'Apple model saved', variant: 'success' })
          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Apple model save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [appleModel])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving Apple template', variant: 'light' })

        let result = await saveAppleTemplate({ apiSource, apiKey, templateId, appleTemplate })

        if (result.ok) {
          enqueueSnackbar({ message: 'Apple template saved', variant: 'success' })
          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Apple template save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [appleTemplate])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving Google class', variant: 'light' })

        let result = await saveGoogleClass({ apiSource, apiKey, templateId, googleClass })

        if (result.ok) {
          enqueueSnackbar({ message: 'Google class saved', variant: 'success' })

          skipGoogleClassSave = true
          setGoogleClass(result.classData)

          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Google class save failed', variant: 'error' })
        }
      }
      if (skipGoogleClassSave) skipGoogleClassSave = false
      else save()
    }
  }, [googleClass])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving Google object', variant: 'light' })

        let result = await saveGoogleObject({ apiSource, apiKey, templateId, googleObject })

        if (result.ok) {
          enqueueSnackbar({ message: 'Google object saved', variant: 'success' })

          skipGoogleObjectSave = true
          setGoogleObject(result.objectData)

          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Google object save failed', variant: 'error' })
        }
      }
      if (skipGoogleObjectSave) skipGoogleObjectSave = false
      else save()
    }
  }, [googleObject])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving Google class & object', variant: 'light' })

        let googleClass = googleBoth.googleClass
        let googleObject = googleBoth.googleObject

        let result = await saveGoogleData({ apiSource, apiKey, templateId, googleClass, googleObject })

        if (result.ok) {
          enqueueSnackbar({ message: 'Google class & object saved', variant: 'success' })

        // Ensure we do not fire the useEffects for googleClass and googleObject
          skipGoogleClassSave = true
          skipGoogleObjectSave = true
          setGoogleClass(result.classData)
          setGoogleObject(result.objectData)

          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Google class & object save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [googleBoth])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving sample data', variant: 'light' })

        let result = await saveInstanceData({ apiSource, apiKey, templateId, instanceData })

        if (result.ok) {
          enqueueSnackbar({ message: 'Sample data saved', variant: 'success' })
          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Sample data save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [instanceData])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving scanner data', variant: 'light' })

        let result = await saveScannerConfiguration({ apiSource, apiKey, templateId, scannerData })

        if (result.ok) {
          enqueueSnackbar({ message: 'Scanner data saved', variant: 'success' })
          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Scanner data save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [scannerData])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving meta data', variant: 'light' })

        let result = await saveMetaData({ apiSource, apiKey, templateId, metaData })

        if (result.ok) {
          enqueueSnackbar({ message: 'Meta data saved', variant: 'success' })
          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Meta data save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [metaData])

  async function loadInitialData() {

    const { metaData, isSmartixScannerPass, smartixPassType, appleModel, appleTemplate, googleObject, instanceData, scannerData, scannerInstanceData,googleClass, applePassType, appleSubType, googlePassType, googleSubType } = await loadTemplate({ apiSource, apiKey, templateId })

    if (metaData.wizardPermitted) navigate("../wizard")

    setSmartixPassType(smartixPassType)
    setApplePassType(applePassType)
    setAppleSubType(appleSubType)
    setGooglePassType(googlePassType)
    setGoogleSubType(googleSubType)
    setIsSmartixScannerPass(isSmartixScannerPass)

    setAppleModel(appleModel)
    setAppleTemplate(appleTemplate)
    setGoogleClass(googleClass)
    setGoogleObject(googleObject)
    setInstanceData(instanceData)
    setMetaData(metaData)
    setScannerData(scannerData)
    setScannerInstanceData(scannerInstanceData)

    // Delay setting this to true until next render cycle to ensure above 
    // useEffects do not pick up our initial load as a change
    setTimeout(e => setTemplateLoaded(true), 100)
  }

  const appleBarcodeTypeToGoogleType = {
    PKBarcodeFormatAztec: "AZTEC",
    PKBarcodeFormatQR: "QR_CODE",
    PKBarcodeFormatPDF417: "PDF_417",
    PKBarcodeFormatCode128: "CODE_128",
    NONE: "NONE"
  }

  const googleBarcodeTypeToAppleType = {
    "AZTEC": "PKBarcodeFormatAztec",
    "QR_CODE": "PKBarcodeFormatQR",
    "PDF_417": "PKBarcodeFormatPDF417",
    "CODE_128": "PKBarcodeFormatCode128",
    NONE: "NONE"
  }

  function syncBarcodeData({ source, barcode }) {
    switch (source) {
      case "apple": {
        if (barcode) {
          const type = appleBarcodeTypeToGoogleType[barcode.format]
          const value = barcode.message
          const alternateText = barcode.altText
          const newBarcode = { type, value, alternateText }

          var newGoogleObject = _.cloneDeep(googleObject)
          newGoogleObject.barcode = newBarcode
          setGoogleObject(newGoogleObject)
        } else {
          var newGoogleObject = _.cloneDeep(googleObject)
          delete newGoogleObject.barcode
          setGoogleObject(newGoogleObject)
        }
        break
      }

      case "google": {
        if (barcode) {
          const format = googleBarcodeTypeToAppleType[barcode.type]
          const message = barcode.value
          const altText = barcode.alternateText
          const newBarcode = { format, message, altText }
          var newAppleTemplate = _.cloneDeep(appleTemplate)
          newAppleTemplate.barcode = newBarcode
          setAppleTemplate(newAppleTemplate)
        } else {
          var newAppleTemplate = _.cloneDeep(appleTemplate)
          delete newAppleTemplate.barcode
          setAppleTemplate(newAppleTemplate)
        }
        break
      }
    }
  }

  function syncBackgroundColour({ source, backgroundColor }) {
    switch (source) {
      case "apple": {

        let cd = _.cloneDeep(googleClass)
        let od = _.cloneDeep(googleObject)

        switch (googlePassType) {
          case "generic":
            {
              od.hexBackgroundColor = backgroundColor
              break
            }

          case "flight":
          case "transit":
          case "eventTicket":
            {
              od.hexBackgroundColor = backgroundColor
              cd.hexBackgroundColor = backgroundColor
              // TODO - how do I want to handle colour in both?
              break
            }

          case "giftCard":
          case "loyalty":
          case "offer":
            {
              cd.hexBackgroundColor = backgroundColor
              break
            }
        }

        setGoogleClass(cd)
        setGoogleObject(od)

        break
      }
      case "google": {
        var m = _.cloneDeep(appleModel)
        m.backgroundColor = backgroundColor
        setAppleModel(m)
        break
      }
    }
  }

  const openInstanceDataManager = () => setShowManageInstanceData(true)

  return (
    <>
      <div className={classes}>
        <Body
          templateId={templateId}
          smartixPassType={smartixPassType}
          appleTemplate={appleTemplate}
          googleObject={googleObject}
          instanceData={instanceData}
          setInstanceData={setInstanceData}
          scannerInstanceData={scannerInstanceData}
          scannerData={scannerData}
          setScannerData={setScannerData}
          appleModel={appleModel}
          googleClass={googleClass}
          applePassType={applePassType}
          appleSubType={appleSubType}
          googlePassType={googlePassType}
          templateLoaded={templateLoaded}
          googleSubType={googleSubType}
          setGoogleBoth={setGoogleBoth}
          setAppleModel={setAppleModel}
          setAppleTemplate={setAppleTemplate}
          setGoogleClass={setGoogleClass}
          setGoogleObject={setGoogleObject}
          syncBarcodeData={syncBarcodeData}
          syncBackgroundColour={syncBackgroundColour}
          isSmartixScannerPass={isSmartixScannerPass}
          metaData={metaData}
          setMetaData={setMetaData}
          openInstanceDataManager={openInstanceDataManager}
        />
      </div>

      <ManageInstanceData open={showManageInstanceData} close={() => setShowManageInstanceData(false)} instanceData={instanceData} setInstanceData={setInstanceData} />
    </>
  )
}

export default Editor